"use client";

import { useLingui } from "@lingui/react/macro";
import { Skeleton } from "@offstreetparking/design-system/feedback";
import { Text } from "@offstreetparking/design-system/typography";
import { useRouter, useSearchParams } from "next/navigation";
import { useState } from "react";
import { routes } from "~/routes";
import { offstreetClient } from "~/services/offstreetApi";
import { CodeMetricsDisplay } from "./components/CodeMetricsDisplay";
import { CodeStep } from "./components/CodeStep";
import { ItemStep } from "./components/ItemStep";
import { LocationStep } from "./components/LocationStep";
import { SelectionSummary } from "./components/SelectionSummary";
import { TenantStep } from "./components/TenantStep";

type SelectedItem = {
	type: "permitType" | "registrationSetting";
	id: number;
	name: string;
};

type CodeEnabledItems = {
	permitTypes: { id: number; name: string }[];
	registrationSettings: { id: number; name: string }[];
};

const parseNumericParam = (value: string | null): number | undefined => {
	if (!value) {
		return undefined;
	}
	const parsed = Number(value);
	return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

const LoadingSkeleton = () => (
	<div className="space-y-4 p-8">
		<Skeleton height={32} width={200} />
		<Skeleton height={48} width={400} />
		<Skeleton height={48} width={400} />
	</div>
);

const MutedMessage = ({ children }: { children: React.ReactNode }) => (
	<div className="px-8 py-4">
		<Text size="sm" intent="muted">
			{children}
		</Text>
	</div>
);

// Prefer permitType over settingId when both happen to match. Returns undefined if no match.
const deriveDeepLinkedItem = ({
	items,
	deepLinkPermitTypeId,
	deepLinkSettingId,
}: {
	items: CodeEnabledItems;
	deepLinkPermitTypeId: number | undefined;
	deepLinkSettingId: number | undefined;
}): SelectedItem | undefined => {
	if (deepLinkPermitTypeId !== undefined) {
		const pt = items.permitTypes.find((p) => p.id === deepLinkPermitTypeId);
		if (pt) {
			return { type: "permitType", id: pt.id, name: pt.name };
		}
	}
	if (deepLinkSettingId !== undefined) {
		const rs = items.registrationSettings.find((s) => s.id === deepLinkSettingId);
		if (rs) {
			return { type: "registrationSetting", id: rs.id, name: rs.name };
		}
	}
	return undefined;
};

// Auto-select when there's exactly one code-enabled item AND the parker didn't explicitly request one.
const deriveAutoSkipItem = ({
	items,
	hasExplicitDeepLinkItem,
}: {
	items: CodeEnabledItems;
	hasExplicitDeepLinkItem: boolean;
}): SelectedItem | undefined => {
	if (hasExplicitDeepLinkItem) {
		return undefined;
	}
	const total = items.permitTypes.length + items.registrationSettings.length;
	if (total !== 1) {
		return undefined;
	}
	if (items.permitTypes.length === 1) {
		const pt = items.permitTypes[0]!;
		return { type: "permitType", id: pt.id, name: pt.name };
	}
	const rs = items.registrationSettings[0]!;
	return { type: "registrationSetting", id: rs.id, name: rs.name };
};

export default function CodeCheckPageContent() {
	const { t } = useLingui();
	const router = useRouter();
	const searchParams = useSearchParams();

	const deepLinkTenantId = parseNumericParam(searchParams.get("tenant"));
	const deepLinkPermitTypeId = parseNumericParam(searchParams.get("permitTypeId"));
	const deepLinkSettingId = parseNumericParam(searchParams.get("settingId"));
	const locationParam = searchParams.get("location") ?? undefined;
	const identifierParam = searchParams.get("identifier") ?? undefined;

	const [locationIdentifier, setLocationIdentifier] = useState<string | undefined>(locationParam);
	const [selectedTenantId, setSelectedTenantId] = useState<number | undefined>();
	const [selectedItem, setSelectedItem] = useState<SelectedItem | undefined>();
	const [submittedCode, setSubmittedCode] = useState<string | undefined>();
	const [deepLinkTenantDismissed, setDeepLinkTenantDismissed] = useState(false);
	const [deepLinkItemDismissed, setDeepLinkItemDismissed] = useState(false);

	// Resolution paths are mutually exclusive: a typed/URL identifier wins over the lookup-id deep-link.
	const usingLocationIdentifier = !!locationIdentifier;
	const shouldResolveLocation = usingLocationIdentifier || (!!identifierParam && !locationParam);

	const { data: resolution, isPending: resolutionPending } =
		offstreetClient.registrationCode.resolveLocationLookup.useQuery(
			usingLocationIdentifier ? { locationIdentifier: locationIdentifier! } : { id: identifierParam! },
			{ enabled: shouldResolveLocation },
		);

	// Strip cached resolution data when neither path is active (post-reset still keys to the
	// previous query under a stale entry, so we drop it explicitly).
	const activeResolution = shouldResolveLocation ? resolution : undefined;

	const effectiveLocationId = activeResolution?.locationId;

	// Cosmetic identifier for the location summary chip — falls back to the typed value.
	const displayLocationIdentifier = activeResolution?.locationIdentifier ?? locationIdentifier;

	const effectiveDeepLinkTenantId = deepLinkTenantId ?? activeResolution?.tenantId;
	const effectiveDeepLinkPermitTypeId = deepLinkPermitTypeId ?? activeResolution?.permitTypeId;
	const effectiveDeepLinkSettingId = deepLinkSettingId ?? activeResolution?.settingId;

	// Fast-path for explicit permit-type deep links where the lookup has no locationId.
	// Bypasses the tenant/item picker and fetches permit type details directly so we
	// can either drop to the code input or show "no codes for item" without enumerating
	// everything code-enabled at the location.
	const isDeepLinkPermitTypeFastPath =
		!!effectiveDeepLinkPermitTypeId && effectiveLocationId === undefined && !deepLinkItemDismissed;

	const { data: deepLinkedPermitTypeDetail, isPending: deepLinkedPermitTypeDetailPending } =
		offstreetClient.locationLookup.getPermitTypeById.useQuery(
			{ permitTypeId: effectiveDeepLinkPermitTypeId! },
			{ enabled: isDeepLinkPermitTypeFastPath },
		);

	const deepLinkedPermitTypeHasCodes = deepLinkedPermitTypeDetail
		? deepLinkedPermitTypeDetail.context?.code !== undefined
		: undefined;

	const fastPathEffectiveItem: SelectedItem | undefined =
		isDeepLinkPermitTypeFastPath && deepLinkedPermitTypeDetail
			? { type: "permitType", id: deepLinkedPermitTypeDetail.id, name: deepLinkedPermitTypeDetail.name }
			: undefined;

	const {
		data: tenantsData,
		isPending: tenantsPending,
		error: tenantsError,
	} = offstreetClient.registrationCode.getCodeEnabledTenants.useQuery(
		{ locationId: effectiveLocationId!, selectedTenantId: deepLinkTenantId },
		{ enabled: effectiveLocationId !== undefined },
	);

	const locationName = tenantsData?.locationName;
	const tenants = tenantsData?.tenants;

	// Derive effective tenant: user selection > deep link > auto-skip (single tenant).
	// Bypass the `tenants.some(...)` membership check when either (a) the URL has an explicit
	// `?tenant=` param or (b) the deep-link identifies a specific item (permitTypeId or
	// settingId). This lets the page honor the item's own portalEnabled flag instead of the
	// tenant's discoverable flag. Downstream queries still enforce item-to-tenant assignment
	// and item portalEnabled=1. The TenantStep picker is unaffected — it receives the
	// already-filtered `tenants` array from getCodeEnabledTenants.
	const hasExplicitUrlTenant = !!deepLinkTenantId;
	const hasDeepLinkedItem = !!effectiveDeepLinkPermitTypeId || !!effectiveDeepLinkSettingId;
	const deepLinkedTenantId =
		!deepLinkTenantDismissed &&
		effectiveDeepLinkTenantId &&
		(hasExplicitUrlTenant || hasDeepLinkedItem || tenants?.some((t) => t.id === effectiveDeepLinkTenantId))
			? effectiveDeepLinkTenantId
			: undefined;
	const effectiveTenantId =
		selectedTenantId ?? deepLinkedTenantId ?? (tenants?.length === 1 ? tenants[0]!.id : undefined);
	const effectiveTenantName =
		tenants?.find((tn) => tn.id === effectiveTenantId)?.name ?? tenantsData?.selectedTenant?.name;

	const {
		data: items,
		isPending: itemsPending,
		error: itemsError,
	} = offstreetClient.registrationCode.getCodeEnabledItems.useQuery(
		{ locationId: effectiveLocationId!, tenantId: effectiveTenantId! },
		{ enabled: effectiveLocationId !== undefined && !!effectiveTenantId },
	);

	const hasExplicitDeepLinkItem =
		!deepLinkItemDismissed &&
		(effectiveDeepLinkPermitTypeId !== undefined || effectiveDeepLinkSettingId !== undefined);
	const deepLinkedItem =
		items && !deepLinkItemDismissed
			? deriveDeepLinkedItem({
					items,
					deepLinkPermitTypeId: effectiveDeepLinkPermitTypeId,
					deepLinkSettingId: effectiveDeepLinkSettingId,
				})
			: undefined;
	const deepLinkItemMissing = !!items && hasExplicitDeepLinkItem && !deepLinkedItem;
	const autoSkipItem = items ? deriveAutoSkipItem({ items, hasExplicitDeepLinkItem }) : undefined;
	const effectiveItem = selectedItem ?? fastPathEffectiveItem ?? deepLinkedItem ?? autoSkipItem;

	const {
		data: permitTypeMetrics,
		isFetching: permitTypeMetricsFetching,
		error: permitTypeMetricsError,
	} = offstreetClient.registrationCode.checkCodeMetrics.useQuery(
		{ code: submittedCode!, permitTypeId: effectiveItem?.id! },
		{ enabled: !!submittedCode && effectiveItem?.type === "permitType" },
	);

	const {
		data: settingMetrics,
		isFetching: settingMetricsFetching,
		error: settingMetricsError,
	} = offstreetClient.registrationCode.checkCodeMetricsForSetting.useQuery(
		{ code: submittedCode!, permitConfigurationId: effectiveItem?.id! },
		{ enabled: !!submittedCode && effectiveItem?.type === "registrationSetting" },
	);

	const metrics = permitTypeMetrics ?? settingMetrics;
	const metricsFetching = permitTypeMetricsFetching || settingMetricsFetching;
	const metricsError = permitTypeMetricsError ?? settingMetricsError;

	const resetFromLocation = () => {
		setLocationIdentifier(undefined);
		setSelectedTenantId(undefined);
		setSelectedItem(undefined);
		setSubmittedCode(undefined);
		setDeepLinkTenantDismissed(false);
		setDeepLinkItemDismissed(false);
		router.push(routes.codeCheck());
	};

	const resetFromTenant = () => {
		setSelectedTenantId(undefined);
		setSelectedItem(undefined);
		setSubmittedCode(undefined);
		setDeepLinkTenantDismissed(true);
		setDeepLinkItemDismissed(true);
	};

	const resetFromItem = () => {
		setSelectedItem(undefined);
		setSubmittedCode(undefined);
		setDeepLinkItemDismissed(true);
	};

	const resetCode = () => {
		setSubmittedCode(undefined);
	};

	const summaries = (levels: Array<"location" | "tenant" | "item">) => {
		const itemCount = items ? items.permitTypes.length + items.registrationSettings.length : 0;
		const canResetTenant = (tenants?.length ?? 0) > 1;
		// Fast-path items stay resettable (reset drops the parker to LocationStep) even though itemCount is 0 there.
		const canResetItem = itemCount > 1 || isDeepLinkPermitTypeFastPath;

		return (
			<>
				{levels.includes("location") && displayLocationIdentifier && (
					<SelectionSummary
						label={t`Location`}
						value={locationName || displayLocationIdentifier}
						subtitle={locationName ? displayLocationIdentifier : undefined}
						onReset={resetFromLocation}
					/>
				)}
				{levels.includes("tenant") && effectiveTenantId && (
					<SelectionSummary
						label={t({
							message: "Tenant",
							comment:
								"Multi-tenancy sense: a permission-bearing group under a company, not a residential renter. Keep FR locataire / ES inquilino.",
						})}
						value={effectiveTenantName ?? String(effectiveTenantId)}
						onReset={canResetTenant ? resetFromTenant : undefined}
					/>
				)}
				{levels.includes("item") && effectiveItem && (
					<SelectionSummary
						label={t({
							message: "Item",
							comment:
								"Code Check wizard step label: the thing the parker selects — a permit type or a registration setting. Not a retail article.",
						})}
						value={effectiveItem.name}
						onReset={canResetItem ? resetFromItem : undefined}
					/>
				)}
			</>
		);
	};

	// Loading either resolution or fast-path permit type detail
	if (
		(shouldResolveLocation && resolutionPending) ||
		(isDeepLinkPermitTypeFastPath && deepLinkedPermitTypeDetailPending)
	) {
		return <LoadingSkeleton />;
	}

	// Fast-path: deep-linked permit type is not configured with registration codes
	if (isDeepLinkPermitTypeFastPath && deepLinkedPermitTypeDetail && deepLinkedPermitTypeHasCodes === false) {
		return (
			<div>
				{summaries(["item"])}
				<MutedMessage>{t`This item has not been configured with registration codes.`}</MutedMessage>
			</div>
		);
	}

	// Manually-entered identifier didn't resolve to any location — show the "no codes" message
	// scoped to the typed identifier rather than looping back to LocationStep. Reset clears state.
	if (usingLocationIdentifier && !resolutionPending && resolution === null) {
		return (
			<div>
				{summaries(["location"])}
				<MutedMessage>{t`This location has no code-enabled items.`}</MutedMessage>
			</div>
		);
	}

	// Location step — only when we're not in fast-path and don't have a resolved location
	if (effectiveLocationId === undefined && !isDeepLinkPermitTypeFastPath) {
		return <LocationStep onSubmit={setLocationIdentifier} />;
	}

	// Loading tenants (skipped in fast-path — the tenant query is disabled there)
	if (tenantsPending && !isDeepLinkPermitTypeFastPath) {
		return <LoadingSkeleton />;
	}

	// No tenants found — but if a deep-linked item resolved an `effectiveTenantId`, skip this
	// gate so the items query can fire for that hidden-from-picker tenant.
	if (tenantsError || (tenants && tenants.length === 0 && !effectiveTenantId)) {
		return (
			<div>
				{summaries(["location"])}
				<MutedMessage>{t`This location has no code-enabled items.`}</MutedMessage>
			</div>
		);
	}

	// Tenant step (skipped in fast-path — deep-linked permit type flow has no tenant picker)
	if (!effectiveTenantId && !isDeepLinkPermitTypeFastPath) {
		return (
			<div>
				{summaries(["location"])}
				<TenantStep tenants={tenants ?? []} onSelect={setSelectedTenantId} />
			</div>
		);
	}

	// Loading items (skipped in fast-path — the items query is disabled there)
	if (itemsPending && !isDeepLinkPermitTypeFastPath) {
		return (
			<div>
				{summaries(["location", "tenant"])}
				<LoadingSkeleton />
			</div>
		);
	}

	// No items found
	if (itemsError || (items && items.permitTypes.length === 0 && items.registrationSettings.length === 0)) {
		return (
			<div>
				{summaries(["location", "tenant"])}
				<MutedMessage>{t`This location has no code-enabled items.`}</MutedMessage>
			</div>
		);
	}

	// Deep-linked item isn't code-enabled — match Portal V2 behavior and refuse to auto-select a different item
	if (deepLinkItemMissing) {
		return (
			<div>
				{summaries(["location", "tenant"])}
				<MutedMessage>{t`This item has not been configured with registration codes.`}</MutedMessage>
			</div>
		);
	}

	// Item step
	if (!effectiveItem) {
		return (
			<div>
				{summaries(["location", "tenant"])}
				<ItemStep
					permitTypes={items?.permitTypes ?? []}
					registrationSettings={items?.registrationSettings ?? []}
					onSelectPermitType={(id, name) => setSelectedItem({ type: "permitType", id, name })}
					onSelectRegistrationSetting={(id, name) =>
						setSelectedItem({ type: "registrationSetting", id, name })
					}
				/>
			</div>
		);
	}

	// Code step
	if (!submittedCode || metricsError) {
		return (
			<div>
				{summaries(["location", "tenant", "item"])}
				<CodeStep isPending={metricsFetching} error={metricsError} onSubmit={setSubmittedCode} />
			</div>
		);
	}

	// Loading metrics
	if (metricsFetching) {
		return (
			<div>
				{summaries(["location", "tenant", "item"])}
				<LoadingSkeleton />
			</div>
		);
	}

	// Results
	if (metrics) {
		return (
			<div>
				{summaries(["location", "tenant", "item"])}
				<CodeMetricsDisplay metrics={metrics} onCheckAnother={resetCode} />
			</div>
		);
	}

	return null;
}
